import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import {
  FIRESTORE_PURCHASE_ORDERS_TABLE,
  FIRESTORE_PRODUCT_DRAFTS_TABLE,
} from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase orders from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @returns {Object} - Purchase orders data and state
 */
export const usePurchaseOrders = (options = {}) => {
  const { realtime = true, status = null, supplierId = null } = options;
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE);

    // Apply filters
    if (status) {
      query = query.where("status", "==", status);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }

    // Order by creation date
    query = query.orderBy("createdAt", "desc");

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPurchaseOrders(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase orders:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPurchaseOrders(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase orders:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, status, supplierId]);

  return { purchaseOrders, loading, error };
};

/**
 * Hook to fetch a single purchase order with real-time listeners for the order,
 * its history subcollection, and productDrafts for linked draft products.
 * @param {string} id - The purchase order document ID
 * @returns {Object} - { order, loading, orderHistory, historyLoading, productDrafts, error }
 */
export const usePurchaseOrder = (id) => {
  const firestore = useFirestore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [productDrafts, setProductDrafts] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
      .doc(id)
      .onSnapshot(
        (doc) => {
          setOrder(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setLoading(false);
        },
        (err) => {
          console.error("Error listening to purchase order:", err);
          setError(err);
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
      .doc(id)
      .collection("history")
      .orderBy("changedAt", "desc")
      .onSnapshot(
        (snapshot) => {
          setOrderHistory(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          setHistoryLoading(false);
        },
        (err) => {
          console.error("Error loading purchase order history:", err);
          setHistoryLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  useEffect(() => {
    if (!order?.products) return;
    const draftIds = order.products.filter((p) => p.productDraftId).map((p) => p.productDraftId);
    if (draftIds.length === 0) return;
    const unsubscribes = draftIds.map((draftId) =>
      firestore
        .collection(FIRESTORE_PRODUCT_DRAFTS_TABLE)
        .doc(draftId)
        .onSnapshot((snap) => {
          if (snap.exists) {
            setProductDrafts((prev) => ({ ...prev, [draftId]: { id: snap.id, ...snap.data() } }));
          }
        }),
    );
    return () => unsubscribes.forEach((u) => u());
  }, [firestore, order?.products]);

  return { order, loading, orderHistory, historyLoading, productDrafts, error };
};
